import React, { CSSProperties, ReactNode, useContext } from "react";
import Flex from "common/Boxes/Flex";
import styled, { useTheme } from "styled-components";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import Text from "common/Typography/Text";
import WarningOutlined from "@ant-design/icons/WarningOutlined";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { WarningWrapper } from "features/bots/BotForm/common/styles";
import { Error } from "@shared/features/bots/BotForm/common/types";
import useBotFormSummary from "@shared/features/bots/BotForm/useBotFormSummary";
import Tooltip from "common/Tooltips/Tooltip";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";

interface BotFormSummaryProps {
  style?: CSSProperties;
  /** New look: plain rows without the tinted box (e.g. in a form footer). */
  variant?: "plain";
}

/**
 * New look: "9860.12 USDT (9860.12 USD)" shows the main amount as the value
 * and the part in parentheses as a quiet secondary figure.
 */
const splitValue = (value: ReactNode, shouldSplit: boolean) => {
  if (!shouldSplit || typeof value !== "string") return value;
  const match = value.match(/^(.+?)\s*(\([^()]*\))$/);
  if (!match) return value;
  return (
    <>
      {match[1]} <SecondaryValue>{match[2]}</SecondaryValue>
    </>
  );
};

const BotFormSummary = ({ style, variant }: BotFormSummaryProps) => {
  const { isNewLook } = useTheme();
  const { errors } = useContext(BotFormContext)!;
  const { summaryItems, maxBotAmountWarningMessage, botAmountValueTitle } =
    useBotFormSummary();

  const maxBotAmountWarning = maxBotAmountWarningMessage &&
    !errors.includes(Error.INSUFFICIENT_AMOUNT) && (
      <WarningWrapper type="warning">
        <WarningOutlined />
        <div>{maxBotAmountWarningMessage}</div>
      </WarningWrapper>
    );

  return (
    <Wrapper style={style} $plain={variant === "plain"}>
      {summaryItems.map((summaryItem, index) => {
        const value = (
          <ItemValue
            as={summaryItem.tooltipContent ? "span" : "div"}
            type={summaryItem.isWarning ? "warning" : "primary"}
            $isMultiline={summaryItem.isMultiline}
          >
            {splitValue(summaryItem.value, isNewLook && !summaryItem.isWarning)}
          </ItemValue>
        );

        return (
          <Flex
            key={index}
            style={
              summaryItem.isMultiline ? { alignItems: "flex-start" } : undefined
            }
          >
            <ItemKeyWrapper>
              <ItemKey>{summaryItem.key}</ItemKey>
              {summaryItem.helperDescription && (
                <HelperTooltip
                  description={summaryItem.helperDescription}
                  analyticsFieldName={String(summaryItem.key)}
                  analyticsPageName="Bot Form Summary"
                  placement="bottom"
                  maxWidth={320}
                />
              )}
            </ItemKeyWrapper>
            {summaryItem.tooltipContent ? (
              <Tooltip
                title={
                  <TooltipContent>
                    {summaryItem.tooltipContent.rows.map((row) => (
                      <TooltipRow key={row.label}>
                        <span>{row.label}</span>
                        <strong>{row.value}</strong>
                      </TooltipRow>
                    ))}
                    {summaryItem.tooltipContent.calculation && (
                      <TooltipCalculation>
                        {summaryItem.tooltipContent.calculation}
                      </TooltipCalculation>
                    )}
                  </TooltipContent>
                }
                styles={{ root: { maxWidth: 320 } }}
                showMobileBottomDrawer={true}
                mobileTitle={botAmountValueTitle}
              >
                <TooltipValue>{value}</TooltipValue>
              </Tooltip>
            ) : (
              value
            )}
          </Flex>
        );
      })}
      {maxBotAmountWarning}
    </Wrapper>
  );
};

export default BotFormSummary;

const Wrapper = styled.div<{ $plain?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  row-gap: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    row-gap: 8px;
    padding: 12px 16px;
    border-radius: 8px;
    background: ${getNewLookTokens(p.theme).well};
    box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
  `}
  ${(p) =>
    p.theme.isNewLook &&
    p.$plain &&
    `
    row-gap: 8px;
    padding: 0;
    border-radius: 0;
    background: none;
    box-shadow: none;
  `}
`;
const ItemKey = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
  ${(p) =>
    p.theme.isNewLook &&
    `
    font-size: 12.5px;
    line-height: 20px;
    font-weight: 500;
    color: ${getNewLookTokens(p.theme).secondary};
    white-space: nowrap;
  `}
`;
const ItemKeyWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const ItemValue = styled(Text)<{ $isMultiline?: boolean }>`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  text-align: right;
  ${(p) =>
    p.theme.isNewLook &&
    `
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${getNewLookTokens(p.theme).ink};
    font-variant-numeric: tabular-nums;
    padding-left: 16px;
  `}
  ${(p) =>
    p.$isMultiline &&
    `
    display: flex;
    flex-direction: column;
    row-gap: 4px;
  `};
`;
const SecondaryValue = styled.span`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12px;
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
`;
const TooltipValue = styled.span`
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
`;
const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  min-width: 220px;
`;
const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 16px;
`;
const TooltipCalculation = styled.div`
  border-top: 1px solid ${(p) => p.theme.colors.lightestBorderGrey};
  padding-top: 8px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
